import React, { useEffect } from 'react';
import { Col, Form, Modal, Row, Select, Input, Alert, Button } from 'antd';
import { TransactionAdjustmentType } from '../enums/adjustment-type.enum';
import useToastContext from '../../../hooks/useToastContext';
import { validateStatus } from '../../../utils/utils';
import usePostRequestUpgrade from '../../../hooks/utils/usePostRequestUpgrade';
import { IssuersBrands } from '../../../utils/IssuersBrands';
import { useAppContext } from '../../../context/appContext';
import './style/transaction-adjustment-modal.css'
import { useNavigate } from 'react-router-dom';
import { MastercardAdjustmentReasonsType, VisaAdjustmentReasonsType } from '../enums/adjustment-brand-reasons-types.enum';
import { AdjustmentStatus } from '../enums/adjustment-status.enum';
import { AdjustmentHelper } from '../helper/adjustment.helper';
import { AdjustmentModalDTO } from '../dto/adjustment-modal.dto';
import { CreateTransactionAdjustmentRequest } from '../dto/create-adjustment-request.dto';
import { getProperDate } from '../../../utils/date';


const ModalTransactionsAdjustment = ({
   txnAdjustment,
   open,
   onCancel
}: {
  txnAdjustment: AdjustmentModalDTO | undefined;
  open?: boolean;
  onCancel?: () => void;
}) => {
  const {
    settings: { issuer },
  } = useAppContext();
  const navigate = useNavigate();

  var defaultValues = undefined
  var viewMode = undefined

  if (txnAdjustment != undefined){
      defaultValues = new CreateTransactionAdjustmentRequest(
      txnAdjustment.transactionId,
      txnAdjustment.cardId,
      txnAdjustment.cardholderId,
      txnAdjustment?.transactionDate?.split(".")[0]?.replace(" ", "T") + "Z"
    )
    viewMode = txnAdjustment.adjustment !== undefined

    if(viewMode)
      defaultValues["transaction_date"] = getProperDate(txnAdjustment?.transactionDate);
  }

  const [form] = Form.useForm<CreateTransactionAdjustmentRequest>();
  const adjustmentType = Form.useWatch('type', form);
  const toast = useToastContext();
  const { postLoading, executePost} = usePostRequestUpgrade({ path: `v1/adjustments` });

  const validateAction = async (values: CreateTransactionAdjustmentRequest) => {
    try {
      const response = await executePost({ data: values });
      if (validateStatus(response?.status)) {
        toast.success('Éxito', `Transacción ajustada correctamente!.`);
      } else {
        throw new Error();
      }
    } catch (e: unknown) {
      toast.error('Error', `Ocurrió un error al ajustar la transacción, porfavor intentá nuevamente.`);
    }
  }

  useEffect(() => {
    if(txnAdjustment !== undefined) {
      form.setFieldValue("transaction_id", txnAdjustment.transactionId);
      if(viewMode){
        form.setFieldValue("reason", txnAdjustment.adjustment.reason);
        form.setFieldValue("type", txnAdjustment.adjustment.type);
        form.setFieldValue("amount", txnAdjustment.adjustment.amount);
        form.setFieldValue("brand_claim_id", txnAdjustment.adjustment.brandClaimId);
        form.setFieldValue("dispute_id", txnAdjustment.adjustment.disputeId);
        form.setFieldValue("chargeback_id", txnAdjustment.adjustment.chargebackId);
      }
    }
  }, [txnAdjustment]);

  useEffect(() => {
    if (adjustmentType === TransactionAdjustmentType.TOTAL && txnAdjustment != undefined) {
      form.setFieldsValue({ "amount": txnAdjustment.transactionAmount });
    } else {
      if(txnAdjustment == undefined)
        form.setFieldsValue({ "amount": null });
    }
  }, [adjustmentType, form]);


  const getOptionsByBrand = () => {
    return issuer?.brand === "VISA" ? VisaAdjustmentReasonsType : issuer?.brand === "MASTERCARD" ? MastercardAdjustmentReasonsType : [];
  }

  return (
    txnAdjustment && (
    <Modal
      open={open}
      title={ !viewMode ? "Ajustar transacción" : "Transacción ajustada" }
      okText={!viewMode ? 'Enviar' : 'Aceptar'}
      cancelText="Cancelar"
      width={700}
      loading={postLoading}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values: CreateTransactionAdjustmentRequest) => {
            if(!viewMode){
              form.resetFields();
              validateAction(values);
            } else {
              onCancel();
            }
          })
          .catch((info) => {
            console.error('Validate Failed:', info);
          });
      }}
    >
      <Form form={form} layout="vertical" name="form_in_modal" initialValues={defaultValues}>
        <Row gutter={24}>
          <Col span={6}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              name="type"
              label="Tipo"
              rules={[{ required: true, message: 'Por favor seleccionar una opcion.' }]}
            >
              <Select
                disabled={viewMode}
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(TransactionAdjustmentType).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Motivo"
              name="reason"
              rules={[{ required: true, message: 'Porfavor ingrese un motivo' }]}
            >
              <Select
                disabled={viewMode}
                placeholder="Seleccionar tipo"
                showSearch
                options={Object.values(getOptionsByBrand()).map((type) => ({ label: type, value: type }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Total"
              name="amount"
            >
              <Input
                disabled={adjustmentType === TransactionAdjustmentType.TOTAL}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Identificador de reclamo"
              name="brand_claim_id"
              rules={[{ required: true, message: 'El Id de reclamo es requerido.' }]}
            >
              <Input
                disabled={viewMode}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Identificador de disputa"
              name="dispute_id"
              rules={[{ required: true, message: 'El Id de disputa es requerido.' }]}
            >
              <Input
                disabled={viewMode}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Identificador de contra cargo"
              name="chargeback_id"
              rules={[{ required: true, message: 'El Id de contra cargo es requerido.' }]}
            >
              <Input
                disabled={viewMode}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="ID Transacción"
              name="transaction_id"
              initialValue={ !viewMode ? txnAdjustment?.transactionId : null }
              hidden={ !viewMode }
            >
              <Input
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Titular"
              name="cardholder_id"
              initialValue={ !viewMode ? txnAdjustment?.cardholderId : null }
              hidden={ !viewMode }
            >
              <Input
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Fecha"
              name="transaction_date"
              initialValue={ !viewMode ? txnAdjustment?.transactionDate : null }
              hidden={ !viewMode }
            >
              <Input
                disabled
              />
            </Form.Item>
          </Col>
          {
            viewMode ? (
              <>
                <Col span={12}>
                  <Form.Item label="Estado" >
                    <Alert style={{'padding': '0.3em 0.5em'}}
                           message={AdjustmentHelper.getStatusTranslation(txnAdjustment?.adjustment.status)}
                           type={txnAdjustment?.adjustment.status === AdjustmentStatus.CREATED ? 'warning' : txnAdjustment?.adjustment.status === AdjustmentStatus.APPROVED ? 'success' : txnAdjustment?.adjustment.status === AdjustmentStatus.REJECTED ? 'error' : 'warning' }
                           showIcon
                    />
                  </Form.Item>
                </Col>
              </>
            ) : null
          }
          <Col span={12}>
            <Form.Item<CreateTransactionAdjustmentRequest>
              label="Tarjeta"
              name="card_id"
              initialValue={ !viewMode ? txnAdjustment?.cardId : null }
              hidden
            >
              <Input
                disabled
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  ))
}

export default ModalTransactionsAdjustment;